import "react-datepicker/dist/react-datepicker.css"

import { useEffect } from "react"

import {
  type BusinessDetails,
  type Company,
  type PartialBusinessDetails,
} from "@boundless/bl-api-client/customer/models"
import { BusinessModelEnumOptions } from "@boundless/bl-api-client/customer/models/BusinessModelEnumLabels"
import { EstimatedRevenueEnumOptions } from "@boundless/bl-api-client/customer/models/EstimatedRevenueEnumLabels"
import { IndustryEnumOptions } from "@boundless/bl-api-client/customer/models/IndustryEnumLabels"
import { TypeOfBusinessEnumOptions } from "@boundless/bl-api-client/customer/models/TypeOfBusinessEnumLabels"
import {
  getCustomerBusinessDetailsRetrieveQueryKey,
  getCustomerCompanyListQueryKey,
  getCustomerCompanyRetrieveQueryKey,
  getCustomerPartialBusinessDetailsRetrieveQueryKey,
  useCustomerBusinessDetailsUpdate,
  useCustomerCompanyCreate,
  useCustomerCompanyPartialUpdate,
  useCustomerPartialBusinessDetailsPartialUpdate,
} from "@boundless/bl-api-client/customer/services"
import {
  CustomerCompanyCreateBody,
  CustomerPartialBusinessDetailsPartialUpdateBody,
} from "@boundless/bl-api-client/customer/services.zod"
import { zodResolver } from "@hookform/resolvers/zod"
import { useQueryClient } from "@tanstack/react-query"
import { useForm } from "react-hook-form"

import { ButtonNext } from "@/components/buttons/ButtonNext"
import { AutosaveStatus } from "@/components/form-elements/AutosaveStatus"
import { FormCheckbox } from "@/components/form-elements/FormCheckbox"
import { FormDatePicker } from "@/components/form-elements/FormDatePicker"
import { FormInput } from "@/components/form-elements/FormInput"
import { FormSection } from "@/components/form-elements/FormSection"
import { FormSelect } from "@/components/form-elements/FormSelect"
import {
  Card,
  CardAction,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Form } from "@/components/ui/form"
import {
  PrefilledFields,
  prefilledFrom,
  prefilledSources,
} from "@/components/ui/PrefilledFields"
import type { ErrorType } from "@/lib/clients"
import { businessDetailsErrorMap } from "@/lib/errors"
import { CompanyFlowContext } from "@/lib/flows/company"
import { useAutosaveDraft } from "@/lib/forms/useAutosaveDraft"
import { applyErrorsToForm, type ErrorResponseType } from "@/lib/forms/utils"

import type { z } from "zod"

import { LoadingButton } from "../ui/button"

// Make field "businessDetails" of businessDetailsFormSchema required, as it's
// optional in CustomerCompanyCreateBody but always present in this form
const businessDetailsFormSchema = CustomerCompanyCreateBody.extend({
  businessDetails: CustomerCompanyCreateBody.shape.businessDetails.unwrap(),
})

type BusinessDetailsFormSchema = z.infer<typeof businessDetailsFormSchema>

/**
 * Returns initial form data for the business details form.
 *
 * Uses a priority cascade to populate form fields:
 * 1. Full business details
 * 2. Partial business details
 * 3. Empty/default values
 *
 * Enum fields are coerced to empty strings when the source lacks the value,
 * ensuring the form receives valid enum values for validation.
 */
function getInitialFormData(
  businessDetails: BusinessDetails | null,
  partialBusinessDetails: PartialBusinessDetails | null,
): BusinessDetailsFormSchema["businessDetails"] {
  const source = businessDetails || partialBusinessDetails

  if (!source) {
    return {
      businessModel: "" as BusinessDetails["businessModel"],
      estimatedRevenue: "" as BusinessDetails["estimatedRevenue"],
      industry: "" as BusinessDetails["industry"],
      isEbankUser: false,
      startDate: "",
      tradeName: "",
      typeOfBusiness: "" as BusinessDetails["typeOfBusiness"],
      websiteUrl: "",
    }
  }

  return {
    businessModel:
      source.businessModel || ("" as BusinessDetails["businessModel"]),
    estimatedRevenue:
      source.estimatedRevenue || ("" as BusinessDetails["estimatedRevenue"]),
    industry: source.industry || ("" as BusinessDetails["industry"]),
    isEbankUser: source.isEbankUser ?? false,
    startDate: source.startDate ?? "",
    tradeName: source.tradeName ?? "",
    typeOfBusiness:
      source.typeOfBusiness || ("" as BusinessDetails["typeOfBusiness"]),
    websiteUrl: source.websiteUrl ?? "",
  }
}

interface Props {
  company: Company | null
  businessDetails: BusinessDetails | null
  partialBusinessDetails: PartialBusinessDetails | null
}

export function BusinessDetailsForm({
  company,
  businessDetails,
  partialBusinessDetails,
}: Props) {
  const queryClient = useQueryClient()
  const flowActorRef = CompanyFlowContext.useActorRef()
  const flowState = CompanyFlowContext.useSelector(
    (state) => state.context.formErrors,
  )
  const goNext = () => flowActorRef.send({ type: "next" })

  const initialValues = {
    legalName: company?.legalName ?? "",
    businessDetails: getInitialFormData(
      businessDetails,
      partialBusinessDetails,
    ),
  }

  const form = useForm<BusinessDetailsFormSchema>({
    resolver: zodResolver(businessDetailsFormSchema, {
      errorMap: businessDetailsErrorMap,
    }),
    defaultValues: initialValues,
  })

  const { mutate: createCompany, isPending: createPending } =
    useCustomerCompanyCreate({
      mutation: {
        onError: (error) =>
          applyErrorsToForm(
            form.setError,
            error as ErrorType<ErrorResponseType>,
          ),
      },
    })

  const { mutate: patchCompany, isPending: patchCompanyPending } =
    useCustomerCompanyPartialUpdate({
      mutation: {
        onError: (error) =>
          applyErrorsToForm(
            form.setError,
            error as ErrorType<ErrorResponseType>,
          ),
      },
    })

  const {
    mutate: updateBusinessDetails,
    isPending: updateBusinessDetailsPending,
  } = useCustomerBusinessDetailsUpdate({
    mutation: {
      onError: (error) => {
        console.log({ error })
        applyErrorsToForm(
          form.setError,
          error as ErrorType<ErrorResponseType>,
          "businessDetails",
        )
      },
    },
  })

  const invalidateQueries = async (companyUuid: string) => {
    const listCompanyKey = getCustomerCompanyListQueryKey()
    const retrieveCompanyKey = getCustomerCompanyRetrieveQueryKey(companyUuid)
    const retrieveBusinessDetailsKey =
      getCustomerBusinessDetailsRetrieveQueryKey(companyUuid)

    return Promise.all([
      queryClient.invalidateQueries({ queryKey: listCompanyKey }),
      queryClient.invalidateQueries({ queryKey: retrieveCompanyKey }),
      queryClient.invalidateQueries({ queryKey: retrieveBusinessDetailsKey }),
    ])
  }

  const companyUuid = company?.uuid

  const { mutateAsync: updateDraft } =
    useCustomerPartialBusinessDetailsPartialUpdate({
      mutation: {
        meta: { shouldDisplayErrorToast: false },
        onSuccess: (draft: PartialBusinessDetails) =>
          queryClient.setQueryData(
            getCustomerPartialBusinessDetailsRetrieveQueryKey(
              companyUuid ?? "",
            ),
            draft,
          ),
      },
    })

  // `legalName` belongs to the Company rather than the draft, and sits outside
  // the prefix, so autosave never touches it.
  //
  // A Company normally exists by now, created by the wizard's business name
  // step. Anyone reaching this screen without one creates it by submitting
  // this very form, and so has nothing to autosave against until they do.
  const autosave = useAutosaveDraft({
    form,
    initialValues,
    schema: CustomerPartialBusinessDetailsPartialUpdateBody,
    enabled: !!companyUuid && !businessDetails,
    prefix: "businessDetails",
    save: (data) => updateDraft({ companyUuid: companyUuid ?? "", data }),
  })

  const handleSubmit = async (formData: BusinessDetailsFormSchema) => {
    // Saving the real details deletes the draft. A save already sent would
    // land after that and recreate one for a completed screen.
    await autosave.cancel()

    if (company) {
      patchCompany(
        { uuid: company.uuid, data: { legalName: formData.legalName } },
        {
          onSuccess: async () => {
            updateBusinessDetails(
              { companyUuid: company.uuid, data: formData.businessDetails },
              {
                onSuccess: async () => {
                  await invalidateQueries(company.uuid)
                  goNext()
                },
              },
            )
          },
        },
      )
    } else {
      createCompany(
        { data: formData },
        {
          onSuccess: async (company: Company) => {
            await invalidateQueries(company.uuid)
            goNext()
          },
        },
      )
    }
  }

  // Busy while submitting waits out a draft save, so it cannot submit twice.
  const isPending =
    createPending ||
    patchCompanyPending ||
    updateBusinessDetailsPending ||
    form.formState.isSubmitting

  // Mirrors the cascade in `getInitialFormData`: the partial only reaches the
  // fields when there are no real details.
  //
  // The business name comes off the company rather than the draft, so it is
  // the one field here still inferred rather than recorded.
  const prefilled = businessDetails
    ? {}
    : {
        ...prefilledFrom({ legalName: company?.legalName }),
        ...prefilledSources(partialBusinessDetails?.prefilledFields, {
          prefix: "businessDetails",
        }),
      }

  // Apply form errors from flow context when available
  useEffect(() => {
    if (flowState) {
      Object.entries(flowState).forEach(([field, message]) => {
        form.setError(field as keyof BusinessDetailsFormSchema, {
          type: "manual",
          message,
        })
      })
      // Clear form errors from flow after applying
      flowActorRef.send({ type: "clearFormErrors" })
    }
  }, [flowState, form, flowActorRef])

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <Card>
          <CardHeader>
            <CardTitle>Business Details</CardTitle>
            <CardAction>
              <AutosaveStatus status={autosave.status} />
            </CardAction>
          </CardHeader>

          <CardContent className="@container/form">
            <PrefilledFields values={prefilled}>
              <FormFields />
            </PrefilledFields>
          </CardContent>

          <CardFooter className="w-full flex-col gap-2 sm:w-auto sm:flex-row">
            {isPending ? (
              <LoadingButton className="w-full sm:w-auto" />
            ) : (
              <ButtonNext className="w-full sm:w-auto" />
            )}
          </CardFooter>
        </Card>
      </form>
    </Form>
  )
}

function FormFields() {
  return (
    <FormSection>
      <FormInput
        className="col-span-2"
        name="legalName"
        label="Business Name"
        placeholder="Business Name"
      />

      <FormInput
        className="col-span-2"
        name="businessDetails.tradeName"
        label="Trade Name (DBA)"
        placeholder="Trade Name (DBA)"
      />

      <FormSelect
        className="col-span-2"
        name="businessDetails.typeOfBusiness"
        options={TypeOfBusinessEnumOptions}
        label="Type Of Business"
        placeholder="Type Of Business"
      />

      <FormSelect
        className="col-span-2"
        name="businessDetails.businessModel"
        options={BusinessModelEnumOptions}
        label="Business Model"
        placeholder="Business Model"
      />

      <FormSelect
        name="businessDetails.industry"
        options={IndustryEnumOptions}
        label="Industry"
        placeholder="Industry"
      />

      <FormInput
        name="businessDetails.websiteUrl"
        label="Website"
        placeholder="Website"
        type="url"
      />

      <FormDatePicker
        name="businessDetails.startDate"
        label="Incorporation Date"
        placeholder="Incorporation Date"
        maxDate={new Date()}
      />

      <FormSelect
        name="businessDetails.estimatedRevenue"
        options={EstimatedRevenueEnumOptions}
        label="Monthly Average Revenue"
        placeholder="Monthly Average Revenue"
      />

      <FormCheckbox
        name="businessDetails.isEbankUser"
        label="Uses an online-only business bank (e.g., Brex, Mercury, Ramp)"
        className="form-checkbox"
      />
    </FormSection>
  )
}
